import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Pagination,
  Chip,
  CircularProgress,
  Divider,
} from "@mui/material";

import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  limit,
} from "firebase/firestore";

import { db, auth } from "../../firebase";

import {
  Assignment,
  FilterList,
  Search,
} from "@mui/icons-material";

const Homeworkreport = () => {
  const uid = auth.currentUser?.uid;

  const [loading, setLoading] = useState(true);

  const [schoolId, setSchoolId] = useState("");
  const [activeYear, setActiveYear] = useState("");

  const [homeworks, setHomeworks] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [filterDate, setFilterDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [teacherFilter, setTeacherFilter] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // ================= FETCH INITIAL =================
  useEffect(() => {
    const fetchInitial = async () => {
      if (!uid) return;

      try {
        // USER
        const userSnap = await getDocs(
          query(collection(db, "users"), where("__name__", "==", uid))
        );

        if (userSnap.empty) {
          setLoading(false);
          return;
        }

        const sId = userSnap.docs[0].data().schoolId;
        setSchoolId(sId);

        // ACTIVE YEAR
        const yearSnap = await getDocs(
          query(
            collection(db, "academicyears"),
            where("schoolId", "==", sId),
            where("status", "==", "Active"),
            limit(1)
          )
        );

        if (!yearSnap.empty) {
          setActiveYear(yearSnap.docs[0].data().name);
        }

        // TEACHERS
        const teacherSnap = await getDocs(
          query(
            collection(db, "users"),
            where("schoolId", "==", sId),
            where("role", "==", "Teacher")
          )
        );

        setTeachers(
          teacherSnap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );

        setLoading(false);
      } catch (err) {
        console.log(err);
        setLoading(false);
      }
    };

    fetchInitial();
  }, [uid]);

  // ================= FETCH HOMEWORK =================
  useEffect(() => {
    if (!schoolId || !activeYear) return;

    const q = query(
      collection(db, "homework"),
      where("schoolId", "==", schoolId),
      where("academicYear", "==", activeYear),
      where("date", "==", filterDate)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setHomeworks(data);
      setPage(1);
    });

    return () => unsubscribe();
  }, [schoolId, activeYear, filterDate]);

  // ================= FILTERED DATA =================
  const filteredHomework = useMemo(() => {
    return homeworks.filter((item) => {
      const matchesTeacher = teacherFilter
        ? item.teacherId === teacherFilter
        : true;

      const matchesClass = classFilter
        ? item.classId === classFilter
        : true;

      const matchesSubject = subjectFilter
        ? item.subjectId === subjectFilter
        : true;

      const matchesSearch =
        search.trim() === ""
          ? true
          : item.title?.toLowerCase().includes(search.toLowerCase()) ||
            item.content?.toLowerCase().includes(search.toLowerCase());

      return (
        matchesTeacher &&
        matchesClass &&
        matchesSubject &&
        matchesSearch
      );
    });
  }, [
    homeworks,
    teacherFilter,
    classFilter,
    subjectFilter,
    search,
  ]);

  // ================= UNIQUE FILTERS =================
  const uniqueClasses = [
    ...new Set(homeworks.map((item) => item.classId)),
  ];

  const uniqueSubjects = [
    ...new Set(homeworks.map((item) => item.subjectId)),
  ];

  // ================= PAGINATION =================
  const paginatedData = filteredHomework.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  if (loading) {
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, bgcolor: "#f4f7f9", minHeight: "100vh" }}>
      {/* ================= HEADER ================= */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          bgcolor: "#1a237e",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "start", md: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Assignment />
            <Typography variant="h6" fontWeight={900}>
              HOMEWORK REPORT
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <FilterList />

            <TextField
              type="date"
              size="small"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              sx={{
                bgcolor: "white",
                borderRadius: 1,
                minWidth: 170,
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* ================= FILTERS ================= */}
      <Card sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography
          variant="subtitle1"
          fontWeight={800}
          color="primary"
          mb={2}
        >
          FILTERS
        </Typography>

        <Grid className="row" spacing={2}>
          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField
              fullWidth
              label="Search Homework"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: "#777" }} />,
              }}
            />
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Teacher"
              value={teacherFilter}
              onChange={(e) => setTeacherFilter(e.target.value)}
            >
              <MenuItem value="">All Teachers</MenuItem>

              {teachers.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  {teacher.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Class"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <MenuItem value="">All Classes</MenuItem>

              {uniqueClasses.map((cls) => (
                <MenuItem key={cls} value={cls}>
                  {cls}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid className="col-sm-3 mb-3" item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Subject"
              value={subjectFilter}
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              <MenuItem value="">All Subjects</MenuItem>

              {uniqueSubjects.map((sub) => (
                <MenuItem key={sub} value={sub}>
                  {sub}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Card>

      {/* ================= SUMMARY ================= */}
      <Grid container spacing={2} mb={2}>
        <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Homework
            </Typography>

            <Typography variant="h5" fontWeight={900}>
              {filteredHomework.length}
            </Typography>
          </Card>
        </Grid>

        <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Academic Year
            </Typography>

            <Typography variant="h6" fontWeight={900}>
              {activeYear}
            </Typography>
          </Card>
        </Grid>

        <Grid className="col-sm-3 mb-3" item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Report Date
            </Typography>

            <Typography variant="h6" fontWeight={900}>
              {filterDate}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* ================= TABLE ================= */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8f9fa",
            borderBottom: "1px solid #eee",
          }}
        >
          <Typography variant="subtitle1" fontWeight={900}>
            HOMEWORK LIST
          </Typography>
        </Box>

        <Table size="small">
          <TableHead sx={{ bgcolor: "#eeeeee" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 900 }}>Sr No</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Class</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Subject</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Teacher</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Details</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  No Homework Found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((item, index) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    {(page - 1) * rowsPerPage + index + 1}
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={item.classId}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>{item.subjectId}</TableCell>

                  <TableCell>
                    <Typography fontWeight={700}>
                      {item.title}
                    </Typography>
                  </TableCell>

                  <TableCell>{item.teacherName}</TableCell>

                  <TableCell>{item.date}</TableCell>

                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.content || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {filteredHomework.length > rowsPerPage && (
          <Box
            display="flex"
            justifyContent="center"
            p={2}
          >
            <Pagination
              count={Math.ceil(filteredHomework.length / rowsPerPage)}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              size="small"
            />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

export default Homeworkreport;